import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import { Box, List, ListItemButton, Stack, Typography } from '@mui/material';
import { useEffect, useRef } from 'react';
import { SessionType } from '../../../types';
import { formatSessionDate } from './sessionDisplay';

interface SessionRailProps {
    sessions: SessionType[];
    selectedIndex: number;
    onSelect: (index: number) => void;
    viewedSessionIds: number[];
    notedSessionIds: number[];
    orientation: 'vertical' | 'horizontal';
}

const RAIL_WIDTH = 224;

const SessionMarkers = ({ viewed, noted }: { viewed: boolean; noted: boolean }) => (
    <Stack direction='row' spacing={0.5} alignItems='center'>
        {noted && <EditNoteRoundedIcon sx={{ fontSize: 18 }} color='primary' titleAccess='Has your note' />}
        {viewed && <CheckCircleRoundedIcon sx={{ fontSize: 16 }} color='success' titleAccess='Viewed' />}
    </Stack>
);

const SessionRail = ({
    sessions,
    selectedIndex,
    onSelect,
    viewedSessionIds,
    notedSessionIds,
    orientation,
}: SessionRailProps) => {
    const selectedRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        selectedRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }, [selectedIndex]);

    if (sessions.length === 0) {
        return null;
    }

    const isHorizontal = orientation === 'horizontal';

    const items = sessions.map((session, index) => {
        const selected = index === selectedIndex;
        const viewed = viewedSessionIds.includes(session.id);
        const noted = notedSessionIds.includes(session.id);

        return (
            <ListItemButton
                key={session.id}
                ref={selected ? selectedRef : undefined}
                selected={selected}
                onClick={() => onSelect(index)}
                aria-current={selected ? 'true' : undefined}
                sx={{
                    borderRadius: 1.5,
                    mb: isHorizontal ? 0 : 0.5,
                    mr: isHorizontal ? 1 : 0,
                    flexShrink: 0,
                    minWidth: isHorizontal ? 172 : undefined,
                    minHeight: 86,
                    alignItems: 'flex-start',
                    flexDirection: 'column',
                    gap: 0.25,
                    border: '1px solid',
                    borderColor: selected ? 'primary.main' : 'transparent',
                    '&.Mui-selected': { bgcolor: 'action.selected', color: 'text.primary' },
                    '&.Mui-selected:hover': { bgcolor: 'action.hover' },
                    '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
                }}
            >
                <Stack direction='row' justifyContent='space-between' alignItems='center' width='100%'>
                    <Typography variant='subtitle2' fontWeight={700} noWrap>
                        {`Session ${index + 1}`}
                    </Typography>
                    <SessionMarkers viewed={viewed} noted={noted} />
                </Stack>
                <Typography variant='caption' color='text.secondary' noWrap>
                    {formatSessionDate(session.date)}
                </Typography>
                <Typography variant='caption' color='text.secondary' noWrap>
                    {`${session.protocols?.length ?? 0} protocols`}
                </Typography>
            </ListItemButton>
        );
    });

    if (isHorizontal) {
        return (
            <Box
                component='nav'
                aria-label='Sessions'
                sx={{
                    display: 'flex',
                    px: 1.5,
                    py: 1,
                    overflowX: 'auto',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                }}
            >
                {items}
            </Box>
        );
    }

    return (
        <Box
            component='nav'
            aria-label='Sessions'
            sx={{
                width: RAIL_WIDTH,
                flexShrink: 0,
                borderRight: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
            }}
        >
            <Typography
                variant='overline'
                sx={{ px: 2, pt: 2, pb: 1, fontWeight: 700, color: 'text.secondary' }}
            >
                {`${sessions.length} Sessions`}
            </Typography>
            <List sx={{ px: 1, pb: 2, overflowY: 'auto', flex: 1 }}>{items}</List>
        </Box>
    );
};

export default SessionRail;
